import Ajv from "ajv";
import { actionIsSentOnCreate } from "../configPanel/actionSetup";
import addFormats from "ajv-formats";
import addKeywords from "ajv-keywords";
import { boundingBoxErrors } from "./boundingBox";

// Shared AJV pass over an integration's action configurations, used by the
// "New Connection" modal and the new-Route wizard — the two screens that commit
// every section in one submit.
//
// It validates EVERY section the screen shows, not just the ones the user
// touched: a required auth field the user never opened has no configuration
// entry at all, and iterating the entries let those screens create records with
// empty credentials.
export type ActionConfigErrors = Record<string, Record<string, string>>;

type Translate = (key: any, options?: any) => string;

// These messages are rendered under the field, so they are the user's copy, not
// AJV's. AJV's own text is English-only and names JSON-schema keywords, which is
// no help to anyone filling in a form — so each keyword maps to one translated
// sentence, with a general fallback for the rest.
const messageFor = (keyword: string, t: Translate): string => {
    switch (keyword) {
        case "required":
            return t("This field is required.");
        case "pattern":
        case "format":
            return t("This value doesn't have the expected format.");
        case "minLength":
        case "minItems":
        case "minimum":
        case "exclusiveMinimum":
            return t("This value is too small or too short.");
        case "maxLength":
        case "maxItems":
        case "maximum":
        case "exclusiveMaximum":
            return t("This value is too large or too long.");
        case "enum":
            return t("Choose one of the available options.");
        case "type":
        default:
            return t("This value is not valid.");
    }
};

const buildAjv = () => {
    const ajv = new Ajv({ allErrors: true, verbose: true, strictTypes: false });
    // Portal-only schema keywords: they carry UI meaning, not validation.
    ajv.addKeyword({ keyword: "is_executable", validate: () => true, errors: false });
    ajv.addKeyword({
        keyword: "example",
        type: "string",
        schemaType: ["string", "object", "number", "array"],
        validate: () => true,
    });
    addFormats(ajv);
    // File widgets submit base64 payloads, which ajv-formats does not know.
    ajv.addFormat("data-url", {
        type: "string",
        validate: (data: string) =>
            /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(data),
    });
    addKeywords(ajv);
    return ajv;
};

// A schema with if/then/else validates against the branch the data selects, so
// the required list depends on what the user picked.
//
// The branch is chosen from the data AS ENTERED, before the null-filling below.
// The two validators this replaced filled missing properties with null first,
// which made an `if: { required: [...] }` match on a field the user never
// entered — the branch was picked as though the field had been filled. Choosing
// from the raw data is the honest reading.
const resolveSchema = (ajv: Ajv, schema: any, data: any) => {
    // A conditional may carry only one arm — `if`/`then` with no `else` is
    // valid JSON Schema and common. Requiring both left the branch's fields out
    // of `required`, so AJV's error for them was discarded as "optional and
    // empty". Same rule as flowMap/validateActionData.
    if (!(schema.if && (schema.then || schema.else))) return schema;
    const branch = ajv.compile(schema.if)(data) ? schema.then : schema.else;
    if (!branch || typeof branch !== "object") return schema;
    return {
        ...schema,
        properties: { ...schema.properties, ...branch.properties },
        // The branch ADDS requirements; it does not replace the base schema's.
        // Taking only the branch list let a base-required field (a tenant, say)
        // through whenever the schema had an if/then/else at all.
        required: Array.from(new Set([...(schema.required ?? []), ...(branch.required ?? [])])),
    };
};

// rjsf leaves untouched fields out of the form data entirely; AJV only reports a
// required field as missing, so fill the gaps with null to get one error per
// empty field.
const withNulls = (schema: any, data: any) => {
    const filled = { ...data };
    Object.keys(schema.properties ?? {}).forEach((key) => {
        if (!filled.hasOwnProperty(key) || filled[key] === undefined) filled[key] = null;
    });
    return filled;
};

// AJV's `required` only asks whether the property EXISTS, so a credential the
// user typed and then cleared ("") satisfies it. "Filled in" is what the rest of
// the app means by required here (see actionSetup.missingRequiredFields), so
// blank values are reported the same way a missing one is.
const isBlank = (value: any) =>
    value === undefined || value === null || value === ""
    || (Array.isArray(value) && value.length === 0);

const blankRequiredFields = (schema: any, data: any): string[] =>
    (Array.isArray(schema.required) ? schema.required : []).filter((field: string) => isBlank(data?.[field]));

// Some conditionals keep every field on screen and only change which ones are
// required — iNaturalist's "choose a project, or enter taxa AND a bounding box".
// Flagging just the branch's fields ("taxa is required") hides the other way
// out: the empty field the `if` looks at (projects) would satisfy the rule too,
// yet it is on no required list, so it got no message at all.
//
// When a blank branch-required field could be excused by filling a blank field
// the `if` looks at, every field in that group gets one message pointing to the
// field descriptions and Help Center. The portal does not try to spell out the
// rule itself: an `if` can test anything, so "fill in X or Y" would be wrong for
// conditions other than "X is empty", and a section's description is general
// copy, not an error. A condition field that already holds a value
// (EarthRanger's authentication type) is a choice the user made, not an
// alternative, so the plain required message stands there.
export const conditionalRequirementMessages = (
    ajv: Ajv,
    schema: any,
    data: any,
    t: Translate = (key: any) => key,
): Record<string, string> => {
    if (!schema?.if || !(schema.then || schema.else)) return {};
    let matched: boolean;
    try {
        matched = ajv.compile(schema.if)(data ?? {}) as boolean;
    } catch (e) {
        return {};
    }
    const branch = matched ? schema.then : schema.else;
    if (!branch || typeof branch !== "object") return {};

    const baseRequired: string[] = Array.isArray(schema.required) ? schema.required : [];
    const missing = (Array.isArray(branch.required) ? branch.required : [])
        .filter((field: string) => !baseRequired.includes(field) && isBlank(data?.[field]));
    if (!missing.length) return {};

    const properties = { ...schema.properties, ...branch.properties };
    const conditionFields = Array.from(new Set<string>([
        ...Object.keys(schema.if.properties ?? {}),
        ...(Array.isArray(schema.if.required) ? schema.if.required : []),
    ])).filter((field) => field in properties && !missing.includes(field) && isBlank(data?.[field]));
    if (!conditionFields.length) return {};

    const message = t("Field required. Consult the Help Center or the field description for more information.");
    return Object.fromEntries([...conditionFields, ...missing].map((field) => [field, message]));
};

// The file widget holds its value as an array; the backend wants the JSON
// string. Applied to a COPY: the configurations these screens pass in are React
// state, and rewriting a widget's value behind its back leaves it rendering a
// string it cannot parse if the submit then fails on another field. Callers run
// this over the payload they are about to send — see serializeConfigurations.
export const serializeFileFields = (schema: any, data: any) => {
    if (!("files" in (schema?.properties ?? {})) || !Array.isArray(data?.files)) return data;
    return { ...data, files: data.files.length ? JSON.stringify(data.files) : "" };
};

// What a creation screen submits: every configuration with its file fields
// serialised, and nothing mutated in place.
export const serializeConfigurations = (actions: any[] | undefined | null, configurations: any[] | undefined | null) =>
    (configurations ?? []).map((config: any) => {
        const action = (actions ?? []).find((a: any) => a.id === config.action);
        if (!action?.schema) return config;
        const data = serializeFileFields(action.schema, config.data ?? {});
        return data === config.data ? config : { ...config, data };
    });

// What a creation screen posts. A switched-off section is left out entirely: the
// API rejects any configuration with empty required fields, and an absent one
// means the action is unconfigured, which is the same "off" (GUNDI-5697). The
// exception is a connector defaulting the action to ON — see actionIsSentOnCreate.
export const configurationsForCreate = (actions: any[] | undefined | null, configurations: any[] | undefined | null) =>
    serializeConfigurations(actions, (configurations ?? []).filter((config: any) => {
        const action = (actions ?? []).find((a: any) => a.id === config.action);
        if (!action) return true;
        return actionIsSentOnCreate(action, config?.data);
    }));

export const validateActionConfigs = (
    actions: any[] | undefined | null,
    configurations: any[] | undefined | null,
    // The screens pass their translator so the field errors speak the user's
    // language. Absent (tests, non-UI callers) the English key is the message.
    t: Translate = (key: any) => key,
): ActionConfigErrors => {
    const errors: ActionConfigErrors = {};
    (actions ?? []).forEach((action: any) => {
        const schema = action?.schema;
        if (!schema?.properties || typeof schema.properties !== "object") return;
        const config = (configurations ?? []).find((c: any) => c.action === action.id);

        const ajv = buildAjv();
        const submitted = serializeFileFields(schema, config?.data ?? {});
        const resolved = resolveSchema(ajv, schema, { ...submitted });
        const data = withNulls(resolved, submitted);
        blankRequiredFields(resolved, config?.data).forEach((field) => {
            if (!errors[action.id]) errors[action.id] = {};
            errors[action.id][field] = messageFor("required", t);
        });
        // Replaces the plain required message on the group, and reaches the
        // condition field AJV never reports.
        Object.entries(conditionalRequirementMessages(ajv, schema, submitted, t)).forEach(([field, message]) => {
            if (!errors[action.id]) errors[action.id] = {};
            errors[action.id][field] = message;
        });

        // A bbox field holding typed text that is not a valid box yet (see boundingBox.ts).
        Object.entries(boundingBoxErrors(action.ui_schema, config?.data, t)).forEach(([field, message]) => {
            if (!errors[action.id]) errors[action.id] = {};
            if (!errors[action.id][field]) errors[action.id][field] = message;
        });

        const validate = ajv.compile(resolved);
        if (validate(data)) return;

        validate.errors?.forEach((error: any) => {
            const field = error.instancePath.slice(1) || error.params.missingProperty;
            const isRequired = Array.isArray(resolved.required) && resolved.required.includes(field);
            // An optional field left empty is not an error.
            if (!isRequired && (data[field] === null || data[field] === undefined)) return;
            // A blank required field is reported as required, whatever keyword
            // AJV happened to trip on (a null fails `type` before `required`).
            const pattern = resolved.properties?.[field]?.pattern;
            const keyword = pattern && !new RegExp(pattern).test(data[field])
                ? "pattern"
                : error.keyword;
            if (!errors[action.id]) errors[action.id] = {};
            // Never overwrite a message already set for this field: the first one
            // is the most specific (the required check runs before AJV).
            if (errors[action.id][field]) return;
            errors[action.id][field] = messageFor(keyword, t);
        });
    });
    return errors;
};
